import { isAnimatable } from '../core/MotionEngine';
import { formatDims } from './format';
import type { WvmDocument } from './types/wvm';

export interface PartListItem {
  id: number;
  name: string;
  code?: string;
  /** mã phân cấp BAZIS (= số trên tem QR), duy nhất toàn model. Optional (vắng ở WVM cũ). */
  designation?: string;
  /** tên cụm (dữ liệu người dùng); '' = chưa có cụm → UI hiển thị nhãn "Khác" theo locale. */
  module: string;
  dims: string;
  animatable: boolean;
  category: string;
}

/** Dựng danh sách item (đã nhóm theo module ở UI). Nhãn động (motion/cụm) dịch lúc render. */
export function buildPartItems(doc: WvmDocument): PartListItem[] {
  return doc.parts.map((p) => ({
    id: p.id,
    name: p.name,
    code: p.code,
    designation: p.designation,
    module: p.module ?? '',
    dims: formatDims(p),
    animatable: isAnimatable(p),
    category: p.category ?? 'panel',
  }));
}

/**
 * "01-23" → "1-23"; giữ nguyên đoạn có chữ. Dùng CHUNG cho quét QR và ô tìm kiếm —
 * QR đệm 0 (pad2) còn ArtPos thì không, nên hai vế phải cùng đi qua đây mới so được.
 * Coi "_" ≡ "-" khi phân cấp: thư viện BAZIS dùng "07_02", tem/QR có thể ra "07-02" — chuẩn hoá
 * cả hai về một dạng để so khớp. Không dùng `String(Number(seg))`: mã dài >15 chữ số mất chính xác.
 */
export function normDesignation(s: string): string {
  return s
    .split(/[-_]/)
    .map((seg) => seg.replace(/^0+(?=\d)/, ''))
    .join('-');
}

/**
 * Lọc danh sách chi tiết theo từ khoá. Hàm THUẦN (không đọc DOM/store) để test được.
 *
 * `exact` = true khi từ khoá đến từ QUÉT QR → chỉ khớp mã, bỏ tìm-text tên/cụm. Thang bậc:
 *   1. `designation` — khoá duy nhất toàn model, đúng phần số trên tem.
 *   2. chỉ khi CẢ MODEL không part nào có `designation` (WVM xuất trước đợt này): lùi về `code`
 *      so với đoạn CUỐI của mã. Kém chính xác (`code` trùng giữa các block) nhưng hơn "không ra gì".
 * Không trộn hai bậc theo từng part: model có designation mà một part thiếu thì part đó KHÔNG khớp,
 * chứ không tụt xuống so `code` — sẽ kéo lại đúng cái nhập nhằng đang chữa.
 */
export function matchParts(items: PartListItem[], q: string, exact: boolean): PartListItem[] {
  const query = q.trim().toLowerCase();
  if (!query) return items;
  const qn = normDesignation(query);

  if (exact) {
    if (items.some((it) => it.designation)) {
      return items.filter((it) => it.designation && normDesignation(it.designation.toLowerCase()) === qn);
    }
    // bậc 2: tem "02-14" ↔ code "14" (đoạn cuối), vẫn chuẩn hoá số 0 hai vế.
    const last = normDesignation(query.split('-').pop() ?? '');
    return items.filter((it) => it.code && normDesignation(it.code.toLowerCase()) === last);
  }

  return items.filter((it) => {
    // mã chi tiết = định danh rời rạc → khớp ĐÚNG BẰNG (tránh "9" khớp 19/29/39);
    // tên + cụm = tìm theo text nên giữ substring.
    const desigExact = !!it.designation && normDesignation(it.designation.toLowerCase()) === qn;
    const codeExact = !!it.code && normDesignation(it.code.toLowerCase()) === qn;
    const textMatch = `${it.name} ${it.module}`.toLowerCase().includes(query);
    return desigExact || codeExact || textMatch;
  });
}

/** Gom item theo module, giữ thứ tự xuất hiện. */
export function groupByModule(items: PartListItem[]): { module: string; items: PartListItem[] }[] {
  const order: string[] = [];
  const map = new Map<string, PartListItem[]>();
  for (const it of items) {
    if (!map.has(it.module)) {
      map.set(it.module, []);
      order.push(it.module);
    }
    map.get(it.module)!.push(it);
  }
  return order.map((module) => ({ module, items: map.get(module)! }));
}
